import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaHome, FaArrowRight, FaCode } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './NotFound.css';

const NotFound = () => {
    const navigate  = useNavigate();
    const canvasRef = useRef(null);

    /* Floating particles on canvas */
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx  = canvas.getContext('2d');
        let raf;

        const resize = () => {
            canvas.width  = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        };
        resize();
        window.addEventListener('resize', resize);

        const particles = Array.from({ length: 40 }, () => ({
            x:   Math.random() * canvas.width,
            y:   Math.random() * canvas.height,
            r:   Math.random() * 2 + 1,
            dx:  (Math.random() - 0.5) * 0.5,
            dy:  (Math.random() - 0.5) * 0.5,
            o:   Math.random() * 0.4 + 0.1,
        }));

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            particles.forEach(p => {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255,159,28,${p.o})`;
                ctx.fill();
                p.x += p.dx;
                p.y += p.dy;
                if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
                if (p.y < 0 || p.y > canvas.height)  p.dy *= -1;
            });
            raf = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('resize', resize);
        };
    }, []);

    return (
        <div className="nf-container">
            <Navbar />

            <section className="nf-hero">
                <div className="nf-grid" />
                <canvas ref={canvasRef} className="nf-canvas" />

                <div className="nf-content">
                    {/* Big 404 */}
                    <motion.div
                        className="nf-code"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, type: 'spring', stiffness: 80 }}
                    >
                        <span className="nf-4">4</span>
                        <span className="nf-zero">
                            <FaCode className="nf-zero-icon" />
                        </span>
                        <span className="nf-4">4</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25, duration: 0.6 }}
                    >
                        Page <span>Not Found</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4, duration: 0.6 }}
                    >
                        Looks like this page took a coffee break. Let's get you back on track.
                    </motion.p>

                    <motion.div
                        className="nf-actions"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.55, duration: 0.6 }}
                    >
                        <button className="nf-btn-primary" onClick={() => navigate('/')}>
                            <FaHome /> Back to Home
                        </button>
                        <button className="nf-btn-secondary" onClick={() => navigate('/contact')}>
                            Contact Us <FaArrowRight />
                        </button>
                    </motion.div>

                    {/* Quick links */}
                    <motion.div
                        className="nf-links"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.75, duration: 0.6 }}
                    >
                        <span>Or try one of these:</span>
                        {[
                            { label: 'Portfolio',  path: '/portfolio' },
                            { label: 'Pricing',    path: '/pricing'   },
                            { label: 'Blog',       path: '/blog'      },
                            { label: 'About',      path: '/about'     },
                        ].map(l => (
                            <button key={l.path} className="nf-quick-link" onClick={() => navigate(l.path)}>
                                {l.label}
                            </button>
                        ))}
                    </motion.div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default NotFound;
